import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { alertError } from '../../api'
import { openQcsSupervisionHandoff } from '../../tabs/account/api/qcsSupervisionHandoff'
import { getBotPackage } from '../../tabs/home/api/botPackage'
import {
  cacheBotPackageAssignment,
  getCachedBotPackageAssignment,
  hasCachedBotPackageAssignment,
} from '../../tabs/home/helpers/orgPackages'

/**
 * QCS toolbar control — left of UserMenu. Opens the supervision app via
 * handoff token from GET /private/qcs-supervision-handoff.
 *
 * Package supervision access depends only on defaultEnabled.
 * Show an access error on click when the feature is unavailable.
 */
const QcsSupervisionButton = ({ botId, activeBot, classes, onVisibleChange }) => {
  const [loading, setLoading] = useState(false)
  const [packageReady, setPackageReady] = useState(false)
  const [supervisionEnabled, setSupervisionEnabled] = useState(false)

  useEffect(() => {
    if (!botId) {
      setPackageReady(true)
      setSupervisionEnabled(false)
      return undefined
    }

    const applyFlags = assignment => {
      setSupervisionEnabled(assignment?.supervision?.defaultEnabled === true)
      setPackageReady(true)
    }

    const seeded =
      (activeBot && Number(activeBot.id) === Number(botId) && activeBot.packageDetails) ||
      (hasCachedBotPackageAssignment(botId) ? getCachedBotPackageAssignment(botId) : null)

    if (seeded) {
      applyFlags(seeded)
    } else {
      // Wait for package access to be resolved before showing the button.
      setPackageReady(false)
      setSupervisionEnabled(false)
    }

    const controller = new AbortController()
    getBotPackage(botId, controller.signal)
      .then(assignment => {
        if (controller.signal.aborted) return
        if (assignment) cacheBotPackageAssignment(botId, assignment)
        applyFlags(assignment)
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          // Keep known package access; never grant access on a failed request.
          setSupervisionEnabled(seeded?.supervision?.defaultEnabled === true)
          setPackageReady(true)
        }
      })

    return () => controller.abort()
  }, [botId, activeBot?.id, activeBot?.packageDetails])

  const visible = Boolean(botId) && packageReady

  useEffect(() => {
    if (onVisibleChange) onVisibleChange(visible)
  }, [visible, onVisibleChange])

  useEffect(() => () => onVisibleChange && onVisibleChange(false), [onVisibleChange])

  if (!botId) return null
  if (!packageReady) return null

  const handleClick = () => {
    if (loading) return
    if (!supervisionEnabled) {
      alertError("You don't have access to this feature")
      return
    }
    setLoading(true)
    openQcsSupervisionHandoff(botId)
      .catch(err => {
        alertError(err?.message || 'Could not open Supervision. Please try again.')
      })
      .finally(() => setLoading(false))
  }

  return (
    <button
      type="button"
      className={classes.supervisionButton}
      onClick={handleClick}
      disabled={loading}
      data-autotest="qcs-supervision-handoff-button">
      {loading ? 'Opening…' : 'Supervision'}
    </button>
  )
}

QcsSupervisionButton.propTypes = {
  botId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  activeBot: PropTypes.object,
  classes: PropTypes.object.isRequired,
  onVisibleChange: PropTypes.func,
}

export default QcsSupervisionButton
